import axios from "axios";
import {AppDispatch, RootState} from "../../app/store";
import {ILabel} from "../../interfaces/Label";
import {calcPxInMM, calcTextWidth} from "../../utils/constants";
import {IItem} from "../../interfaces/Item";
import {BARCODE, HORIZONTAL_LINE, PICTURE, SIMPLE_TEXT, VARIABLE_PICTURE, VARIABLE_TEXT, VERTICAL_LINE} from "../../utils/ItemTypes";
import {IMPORT_XML, SET_RECID} from "../MainReducer";
import i18n from "i18next";
import { showErrorToast } from "../../utils/showToast";
import { ActionCreators as UndoActionCreators } from "redux-undo";

export const ImportXml = (xml: any) => {
  return async (dispatch: AppDispatch) => {
    try {
      const formData = new FormData();
      formData.append("xml", xml);
      const response = await axios.post(`api/ImportXML/ImportXml`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.status === 200 && response.data.success) {
        const labelObject = response.data.root;
        dispatch(ParseObjectToLabelItems(labelObject));
        dispatch({type: SET_RECID, recid: -1, templateName: xml.name.replace(".xml", '') || ""});
      }
    } catch (error) {
      showErrorToast(i18n.t("export_xml_error") + ". " + error);
    }
  };
};

export const ImportXmlFromTemplate = (recid: any, templateName: string, fromNewBtn: boolean) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await axios.post(`api/ImportXML/ImportXmlFromTemplate/${recid}`);
      if (response.status === 200 && response.data.success) {
        const labelObject = response.data.root;
        dispatch(ParseObjectToLabelItems(labelObject));
        if(fromNewBtn){
          dispatch({type: SET_RECID, recid: -1, templateName: ""});          
        } else {
          dispatch({type: SET_RECID, recid: recid, templateName: templateName});
        }
      }
    } catch (error) {
      showErrorToast(i18n.t("export_xml_error") + ". "  + error);
    }
  };
};

export const ParseObjectToLabelItems = (root: any) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const daxLabel = root.daxLabel;
      const labelSizes = getState().refData.labelSizes;
      const size = labelSizes.find((s: any) => s.width === daxLabel.width && s.height === daxLabel.height);
      let selectedLabelSizeId = -1;
      if (size) {
        selectedLabelSizeId = size.id;
      }
      const labelItems: IItem[] = [];
      const labelParams: ILabel = {
        sizeInMM: daxLabel.width.toString() + "x" + daxLabel.height.toString(),
        orientation: daxLabel.orientation,
        dpi: daxLabel.dpi,
        zoom: 1,
        widthInPx: daxLabel.width * calcPxInMM(daxLabel.dpi),
        heightInPx: daxLabel.height * calcPxInMM(daxLabel.dpi),
        showDefaultValues: daxLabel.showDefaultValues === "True",
        pxInMM: calcPxInMM(daxLabel.dpi),
        defaultPath: "",
        gridCellWidth: daxLabel.gridCellWidth ? daxLabel.gridCellWidth : 0,
        showGrid: daxLabel.gridCellWidth && daxLabel.gridCellWidth > 0 ? true : false,
        selectedLabelSizeId: selectedLabelSizeId,
      };

      const barPictureList = root.barPictureList;
      if (barPictureList !== null && barPictureList.length > 0) {
        barPictureList.forEach(async (bar: any) => {
          let item: IItem = {
            itemid: Math.floor(Number((labelItems.length + 1).toString() + (Math.random() * 100000000).toString())),
            id: "barcode",
            text: i18n.t("barcode"),
            iconName: "barcode",
            type: BARCODE,
            xInMM: bar.x,
            yInMM: bar.y,
            selected: false,
            style: {
              position: "absolute",
              top: `${Number(bar.y) * calcPxInMM(daxLabel.dpi)}px`,
              left: `${Number(bar.x) * calcPxInMM(daxLabel.dpi)}px`,
            },
            rotation: bar.rotation,
            value: `data:image/jpeg;base64,${bar.barcodeImg}` || "",
            captionSize: bar.captionSize,
            dataMatrixSize: bar.dataMatrixSize,
            defaultText: bar.defaultText,
            height: bar.height,
            width: bar.width,
            name: bar.name,
            showCaption: bar.showCaption === 1,
            barcodeType: bar.type,
            bringBack: bar.bringBack ? 1 : 2,
          };
          labelItems.push(item);
        });
      }
      const staticTextList = root.staticTextList;
      if (staticTextList !== null && staticTextList.length > 0) {
        staticTextList.forEach((t: any) => {
          const item: IItem = {
            itemid: Math.floor(Number((labelItems.length + 1).toString() + (Math.random() * 100000000).toString())),
            id: "simple-text",
            text: i18n.t("simple_text"),
            iconName: "header",
            type: SIMPLE_TEXT,
            xInMM: t.x,
            yInMM: t.y,
            selected: false,
            style: {
              position: "absolute",
              top: `${Number(t.y) * calcPxInMM(daxLabel.dpi)}px`,
              left: `${Number(t.x) * calcPxInMM(daxLabel.dpi)}px`,
              transform: `rotate(${t.rotation}deg)`,
            },
            value: t.caption,
            rotation: t.rotation,
            fontBold: t.bold === 1,
            fontSize: t.fontSize,
            fontName: t.family,
            width: calcTextWidth(t.caption, t.fontSize, t.family, daxLabel.dpi, t.bold === 1 ? 'bold' : 'normal'),
            bringBack: t.bringBack ? 1 : 2,
          };
          labelItems.push(item);
        });
      }

      const variableTextList = root.variableTextList;
      if (variableTextList !== null && variableTextList.length > 0) {
        variableTextList.forEach((t: any) => {
          const item: IItem = {
            itemid: Math.floor(Number((labelItems.length + 1).toString() + (Math.random() * 100000000).toString())),
            id: "variable-text",
            text: i18n.t("variable_text"),
            iconName: "h",
            type: VARIABLE_TEXT,
            xInMM: t.x,
            yInMM: t.y,
            selected: false,
            style: {
              position: "absolute",
              top: `${Number(t.y) * calcPxInMM(daxLabel.dpi)}px`,
              left: `${Number(t.x) * calcPxInMM(daxLabel.dpi)}px`,
              transform: `rotate(${t.rotation}deg)`,
            },
            value: t.name || t.defaultText,
            rotation: t.rotation,
            align: t.align,
            fontBold: t.bold === 1,
            fontSize: t.fontSize,
            fontName: t.family,
            name: t.name,
            wrap: t.wrap === 1,
            defaultText: t.defaultText,
            width: t.width === 0 ? calcTextWidth(t.name || "", t.fontSize, t.family, daxLabel.dpi, t.bold === 1 ? 'bold' : 'normal') : t.width,
            bringBack: t.bringBack ? 1 : 2,
          };
          labelItems.push(item);
        });
      }

      const staticPictureList = root.staticPictureList;
      if (staticPictureList !== null && staticPictureList.length > 0) {
        staticPictureList.forEach((p: any) => {
          const item: IItem = {
            itemid: Math.floor(Number((labelItems.length + 1).toString() + (Math.random() * 100000000).toString())),
            id: "picture",
            text: i18n.t("picture"),
            iconName: "picture",
            type: PICTURE,
            xInMM: p.x,
            yInMM: p.y,
            selected: false,
            style: {
              position: "absolute",
              top: `${Number(p.y) * calcPxInMM(daxLabel.dpi)}px`,
              left: `${Number(p.x) * calcPxInMM(daxLabel.dpi)}px`,
            },
            rotation: 0,
            value: p.image ? `data:image/jpeg;base64,${p.image}` : p.base64 ? p.base64 : "",
            width: p.width,
            height: p.height,
            path: p.path,
            keepRatio: true,
            name: p.name,
            bringBack: p.bringBack ? 1 : 2,
            base64: p.base64,
          };
          labelItems.push(item);
        });
      }
      const variablePictureList = root.variablePictureList;
      if (variablePictureList !== null && variablePictureList.length > 0) {
        variablePictureList.forEach((p: any) => {
          const item: IItem = {
            itemid: Math.floor(Number((labelItems.length + 1).toString() + (Math.random() * 100000000).toString())),
            id: "picture",
            text: i18n.t("picture"),
            iconName: "picture",
            type: VARIABLE_PICTURE,
            xInMM: p.x,
            yInMM: p.y,
            selected: false,
            style: {
              position: "absolute",
              top: `${Number(p.y) * calcPxInMM(daxLabel.dpi)}px`,
              left: `${Number(p.x) * calcPxInMM(daxLabel.dpi)}px`,
            },
            rotation: 0,
            value: p.image ? `data:image/jpeg;base64,${p.image}` : p.base64 ? p.base64 : "",
            width: p.width,
            height: p.height,
            path: p.path,
            keepRatio: true,
            name: p.name,
            bringBack: p.bringBack ? 1 : 2,
            base64: p.base64,
          };
          labelItems.push(item);
        });
      }

      const lineList = root.lineList;
      if (lineList !== null && lineList.length > 0) {
        lineList.forEach((l: any) => {
          let isVertical = false;
          const angle = Math.atan2(l.y_to - l.y_from, l.x_to - l.x_from) * (180 / Math.PI); //calculate angle between two points and x-axis in degrees
          const threshold = 5; 
          if (Math.abs(angle) < threshold || Math.abs(angle - 180) < threshold || Math.abs(angle + 180) < threshold) {
            l.y_to = l.y_from;
          } else if (Math.abs(angle - 90) < threshold || Math.abs(angle + 90) < threshold) {
            l.x_to = l.x_from;
            isVertical = true;
          }
          const item: IItem = {
            itemid: Math.floor(Number((labelItems.length + 1).toString() + (Math.random() * 100000000).toString())),
            id: !isVertical ? "horizontal-line" : "vertical-line",
            text: i18n.t("line"),
            iconName: !isVertical ? "arrows alternate horizontal" : "arrows alternate vertical",
            type: !isVertical ? HORIZONTAL_LINE : VERTICAL_LINE,
            xInMM: l.x_from,
            yInMM: l.y_from,
            selected: false,
            style: {
              position: "absolute",
              top: `${Number(l.y_from) * calcPxInMM(daxLabel.dpi)}px`,
              left: `${Number(l.x_from) * calcPxInMM(daxLabel.dpi)}px`,
            },

            yToInMM: l.y_to,
            xToInMM: l.x_to,
            rotation: 0,
            value: "",
            lineWidth: l.width,
            width: isVertical ? Math.abs(l.y_to - l.y_from) : Math.abs(l.x_to - l.x_from),
            bringBack: l.bringBack ? 1 : 2,
          };
          labelItems.push(item);
        });
      }
      if (labelItems.length > 0) {
        labelItems[0].selected = true;
      }
      dispatch({type: IMPORT_XML, labelItems: labelItems, labelParams: labelParams});
      dispatch(UndoActionCreators.clearHistory());

    } catch (error) {
      showErrorToast(i18n.t("parse_xml_error") + ". " + error);
    }
  };
};
